import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BarChartIcon from '@mui/icons-material/BarChart';
import { adminAPI, subscriptionPlansAPI, billsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotifyContext';
import AccountSettingsPanel from '../AccountSettingsPanel';
import UserAvatar from '../UserAvatar';
import { userDisplayLabel } from '../../utils/userDisplayLabel';
import { stripeProductDashboardUrl, stripePriceDashboardUrl } from '../../config/payments';
import Pagination from '../Pagination';
import { formatLocalDate, formatLocalDateTime } from '../../utils/formatLocalDate';
import { filterBySearch } from '../../utils/tableSearch';
import TableToolbar from '../TableToolbar';
import Modal from '../Modal';
import PromptModal from '../PromptModal';
import DashboardSectionLoader from '../DashboardSectionLoader';
import AdminDemographicsOptionsPanel from './AdminDemographicsOptionsPanel';
import '../../App.css';

const LOGS_PAGE_SIZE = 25;

const AUDIT_ACTION_OPTIONS = [
  { value: 'all', label: 'All actions' },
  { value: 'signup', label: 'Sign up' },
  { value: 'login', label: 'Login' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'status_change', label: 'Status change' },
  { value: 'file_upload', label: 'File upload' },
  { value: 'file_delete', label: 'File delete' },
  { value: 'export', label: 'Export' },
  { value: 'import_parse', label: 'Import' }
];

function auditBadgeClass(actionKey) {
  switch (actionKey) {
    case 'delete':
    case 'file_delete':
      return 'badge-danger';
    case 'login':
    case 'export':
      return 'badge-info';
    case 'signup':
    case 'create':
    case 'file_upload':
    case 'import_parse':
      return 'badge-success';
    default:
      return 'badge-warning';
  }
}

/** Readable Stripe IDs + deep links to Dashboard (set REACT_APP_STRIPE_TEST_MODE=true for test keys). */
const SubscriptionPlanStripePanel = ({ plan }) => {
  if (!plan.stripeProductId && !plan.stripePriceMonthlyId && !plan.stripePriceYearlyId) {
    return null;
  }
  const linkRow = (label, id, href) => {
    if (!id) {
      return (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: '2px' }}>{label}</div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>—</span>
        </div>
      );
    }
    return (
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: '4px' }}>{label}</div>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '0.7rem',
            lineHeight: 1.4,
            wordBreak: 'break-all',
            color: 'var(--logo-cyan-teal)',
            textDecoration: 'underline',
            textUnderlineOffset: '2px'
          }}
        >
          {id}
        </a>
      </div>
    );
  };
  return (
    <div
      style={{
        marginBottom: '12px',
        padding: '12px',
        borderRadius: '8px',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: '8px' }}>
        Stripe
      </div>
      {linkRow('Product', plan.stripeProductId, stripeProductDashboardUrl(plan.stripeProductId))}
      {linkRow('Monthly price', plan.stripePriceMonthlyId, stripePriceDashboardUrl(plan.stripePriceMonthlyId))}
      {linkRow('Yearly price', plan.stripePriceYearlyId, stripePriceDashboardUrl(plan.stripePriceYearlyId))}
    </div>
  );
};

const AdminDashboard = ({ activeTab: propTab }) => {
  /** Must match URL/parent immediately — internal state lagged one frame and skipped audit-log fetches */
  const activeTab = propTab || 'overview';
  const { user: authUser, refreshUser } = useAuth();
  const { showSuccess, showError } = useNotify();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [reports, setReports] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [crudLogs, setCrudLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logActionFilter, setLogActionFilter] = useState('all');
  const [logResourceFilter, setLogResourceFilter] = useState('all');
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo] = useState('');
  const [logsSearchDebounced, setLogsSearchDebounced] = useState('');
  const [auditResourceTypes, setAuditResourceTypes] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDetails, setShowReportDetails] = useState(false);
  const [userFilterRole, setUserFilterRole] = useState('');
  const [userFilterStatus, setUserFilterStatus] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [walletSearchQuery, setWalletSearchQuery] = useState('');
  const [reportSearchQuery, setReportSearchQuery] = useState('');
  const [billsSearchQuery, setBillsSearchQuery] = useState('');
  const [logsSearchQuery, setLogsSearchQuery] = useState('');
  const [disputesSearchQuery, setDisputesSearchQuery] = useState('');
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [staffStatusFilter, setStaffStatusFilter] = useState('');
  const [planFormMessage, setPlanFormMessage] = useState('');
  const [newPlan, setNewPlan] = useState({
    name: '',
    role: 'lab',
    type: 'basic',
    priceMonthly: '',
    priceYearly: '',
    yearlyDiscountPercent: '',
    clinicsLimit: '5',
    jobPostingLimit: '20',
    offerLimit: '30',
    biddingLimit: '5',
    trialDays: '0',
    features: ''
  });
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [editPlanMessage, setEditPlanMessage] = useState('');
  const [payoutModal, setPayoutModal] = useState(null);

  // Pagination states
  const [usersPage, setUsersPage] = useState(1);
  const [walletsPage, setWalletsPage] = useState(1);
  const [reportsPage, setReportsPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, walletsRes, reportsRes, plansRes] = await Promise.all([
        adminAPI.getDashboardStats(),
        adminAPI.getAllUsers(),
        adminAPI.getAllWallets(),
        adminAPI.getAllReports(),
        subscriptionPlansAPI.getPlans({ params: { includeInactive: '1' } })
      ]);
      if (statsRes.data?.stats) setStats(statsRes.data.stats);
      if (usersRes.data?.users) setUsers(usersRes.data.users);
      if (walletsRes.data?.wallets) setWallets(walletsRes.data.wallets);
      if (reportsRes.data?.reports) setReports(reportsRes.data.reports);
      if (plansRes.data?.byRole) setSubscriptionPlans(plansRes.data.byRole);
      else if (plansRes.data?.plans) {
        const byRole = { lab: [], supplier: [], phlebotomist: [] };
        plansRes.data.plans.forEach(p => { if (byRole[p.role]) byRole[p.role].push(p); });
        setSubscriptionPlans(byRole);
      }
      const billsRes = await billsAPI.getBills();
      if (billsRes.data?.bills) setBills(billsRes.data.bills);
    } catch (err) {
      console.error('Admin fetch error:', err);
      showError(err.response?.data?.message || 'Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateSubscriptionPlan = async (e) => {
    e.preventDefault();
    if (!newPlan.name.trim()) {
      setPlanFormMessage('Name is required');
      return;
    }
    try {
      const features = newPlan.features.split('\n').map((s) => s.trim()).filter(Boolean);
      const { data } = await subscriptionPlansAPI.createPlan({
        name: newPlan.name.trim(),
        role: newPlan.role,
        type: newPlan.type,
        priceMonthly: Number(newPlan.priceMonthly) || 0,
        priceYearly: Number(newPlan.priceYearly) || 0,
        yearlyDiscountPercent: Number(newPlan.yearlyDiscountPercent) || 0,
        trialDays: Number(newPlan.trialDays) || 0,
        clinicsLimit: Number(newPlan.clinicsLimit) || 1,
        jobPostingLimit: Number(newPlan.jobPostingLimit) || 5,
        offerLimit: Number(newPlan.offerLimit) || 30,
        biddingLimit: Number(newPlan.biddingLimit) || 5,
        features,
        isActive: true
      });
      const createdMsg = data?.message || 'Plan created.';
      setPlanFormMessage(createdMsg);
      showSuccess(createdMsg);
      setNewPlan((p) => ({
        ...p,
        name: '',
        features: ''
      }));
      await fetchData();
    } catch (err) {
      const d = err.response?.data;
      const errMsg = d?.detail || d?.message || 'Failed to create plan';
      setPlanFormMessage(errMsg);
      showError(errMsg);
    }
    setTimeout(() => setPlanFormMessage(''), 5000);
  };

  const openEditPlan = (plan) => {
    setEditPlan({
      _id: plan._id,
      name: plan.name || '',
      type: plan.type || 'basic',
      role: plan.role,
      priceMonthly: String(plan.priceMonthly ?? ''),
      priceYearly: String(plan.priceYearly ?? ''),
      yearlyDiscountPercent: String(plan.yearlyDiscountPercent ?? ''),
      trialDays: String(plan.trialDays ?? '0'),
      clinicsLimit: String(plan.clinicsLimit ?? ''),
      jobPostingLimit: String(plan.jobPostingLimit ?? ''),
      offerLimit: String(plan.offerLimit ?? ''),
      biddingLimit: String(plan.biddingLimit ?? ''),
      features: (plan.features || []).join('\n'),
      isActive: plan.isActive !== false
    });
    setEditPlanMessage('');
    setEditPlanOpen(true);
  };

  const handleSaveEditPlan = async (e) => {
    e.preventDefault();
    if (!editPlan?._id || !editPlan.name?.trim()) {
      setEditPlanMessage('Name is required');
      return;
    }
    try {
      const features = editPlan.features.split('\n').map((s) => s.trim()).filter(Boolean);
      const { data } = await subscriptionPlansAPI.updatePlan(editPlan._id, {
        name: editPlan.name.trim(),
        type: editPlan.type,
        priceMonthly: Number(editPlan.priceMonthly) || 0,
        priceYearly: Number(editPlan.priceYearly) || 0,
        yearlyDiscountPercent: Number(editPlan.yearlyDiscountPercent) || 0,
        trialDays: Number(editPlan.trialDays) || 0,
        clinicsLimit: Number(editPlan.clinicsLimit) || 1,
        jobPostingLimit: Number(editPlan.jobPostingLimit) || 5,
        offerLimit: Number(editPlan.offerLimit) || 30,
        biddingLimit: Number(editPlan.biddingLimit) || 5,
        features,
        isActive: !!editPlan.isActive
      });
      const updMsg = data?.message || 'Plan updated.';
      setEditPlanMessage(updMsg);
      showSuccess(updMsg);
      await fetchData();
      setEditPlanOpen(false);
      setEditPlan(null);
    } catch (err) {
      const d = err.response?.data;
      const errMsg = d?.detail || d?.message || 'Failed to update plan';
      setEditPlanMessage(errMsg);
      showError(errMsg);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setLogsSearchDebounced(logsSearchQuery), 400);
    return () => clearTimeout(t);
  }, [logsSearchQuery]);

  useEffect(() => {
    setLogsPage(1);
  }, [logActionFilter, logResourceFilter, logDateFrom, logDateTo, logsSearchDebounced]);

  const fetchAuditLogs = useCallback(async () => {
    if (activeTab !== 'logs') return;
    setLogsLoading(true);
    try {
      const params = {
        page: logsPage,
        limit: LOGS_PAGE_SIZE
      };
      if (logActionFilter && logActionFilter !== 'all') params.action = logActionFilter;
      if (logResourceFilter && logResourceFilter !== 'all') params.resourceType = logResourceFilter;
      if (logDateFrom) params.from = logDateFrom;
      if (logDateTo) params.to = logDateTo;
      if (logsSearchDebounced.trim()) params.q = logsSearchDebounced.trim();

      const { data } = await adminAPI.getAuditLogs(params);
      const logs = Array.isArray(data?.logs) ? data.logs : [];
      setCrudLogs(logs);
      setLogsTotal(typeof data?.total === 'number' ? data.total : logs.length);
      setLogsTotalPages(Math.max(1, typeof data?.totalPages === 'number' ? data.totalPages : 1));
    } catch (err) {
      console.error('Audit logs fetch failed', err);
      setCrudLogs([]);
      setLogsTotal(0);
      setLogsTotalPages(1);
    } finally {
      setLogsLoading(false);
    }
  }, [
    activeTab,
    logsPage,
    logActionFilter,
    logResourceFilter,
    logDateFrom,
    logDateTo,
    logsSearchDebounced
  ]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  useEffect(() => {
    if (activeTab !== 'logs') return;
    (async () => {
      try {
        const { data } = await adminAPI.getAuditLogResourceTypes();
        setAuditResourceTypes(data.resourceTypes || []);
      } catch (err) {
        console.error('Audit resource types fetch failed', err);
      }
    })();
  }, [activeTab]);

  useEffect(() => {
    setUsersPage(1);
  }, [userSearchQuery, userFilterRole, userFilterStatus]);

  useEffect(() => {
    setWalletsPage(1);
  }, [walletSearchQuery]);

  useEffect(() => {
    setReportsPage(1);
  }, [reportSearchQuery]);

  const handleUserStatusToggle = async (userId, isActive) => {
    try {
      const { data } = await adminAPI.updateUserStatus(userId, { isActive: !isActive });
      setUsers(prev => prev.map(u => u._id === userId ? data.user : u));
      showSuccess('User status updated.');
    } catch (err) {
      console.error('Failed to update user status', err);
      showError(err.response?.data?.message || 'Could not update user status.');
    }
  };

  const handleProcessPayout = async (phlebotomistId, amount) => {
    const payoutAmount = parseFloat(String(amount).replace(/,/g, ''));
    if (Number.isNaN(payoutAmount) || payoutAmount <= 0) {
      showError('Please enter a valid amount.');
      return false;
    }
    try {
      await adminAPI.processPayout(phlebotomistId, {
        amount: payoutAmount,
        description: `Admin payout $${payoutAmount}`
      });
      const walletsRes = await adminAPI.getAllWallets();
      if (walletsRes.data?.wallets) setWallets(walletsRes.data.wallets);
      showSuccess(`Payout of $${payoutAmount.toFixed(2)} processed successfully.`);
      return true;
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Payout failed.');
      return false;
    }
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleViewReportDetails = (report) => {
    setSelectedReport(report);
    setShowReportDetails(true);
  };

  const handleMarkReportBilled = async (reportId) => {
    try {
      const { data } = await adminAPI.updateReportBilling(reportId, { billingStatus: 'billed' });
      setReports((prev) => prev.map((report) => (report._id === reportId ? data.report : report)));
      showSuccess('Report marked as billed.');
      return true;
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Update failed.');
      return false;
    }
  };

  const roleAndStatusUsers = users.filter((u) => {
    if (userFilterRole && u.role !== userFilterRole) return false;
    if (userFilterStatus === 'active' && !u.isActive) return false;
    if (userFilterStatus === 'inactive' && u.isActive) return false;
    return true;
  });

  const filteredUsers = useMemo(
    () =>
      filterBySearch(roleAndStatusUsers, userSearchQuery, (u) => [
        u.name,
        u.email,
        u.role,
        u.labName,
        u.clinicName,
        u.companyName
      ]),
    [roleAndStatusUsers, userSearchQuery]
  );

  const filteredWallets = useMemo(
    () =>
      filterBySearch(wallets || [], walletSearchQuery, (w) => [
        w.phlebotomist?.name,
        w.phlebotomist?.email,
        w.balance,
        w.totalEarnings
      ]),
    [wallets, walletSearchQuery]
  );

  const filteredReports = useMemo(
    () =>
      filterBySearch(reports || [], reportSearchQuery, (r) => [
        r.reportNumber,
        r.patientName,
        r.billingStatus,
        r.lab?.labName,
        r.clinic?.clinicName
      ]),
    [reports, reportSearchQuery]
  );

  const filteredBills = useMemo(
    () =>
      filterBySearch(bills || [], billsSearchQuery, (b) => [
        b.invoiceNumber,
        b.type,
        b.status,
        b.biller?.name,
        b.biller?.companyName,
        b.lab?.labName
      ]),
    [bills, billsSearchQuery]
  );

  const filteredDisputes = useMemo(
    () =>
      filterBySearch(disputes || [], disputesSearchQuery, (d) => [
        d.title,
        d.description,
        d.status,
        d.raisedBy?.name,
        d.raisedBy?.labName,
        d.against?.name,
        d.against?.companyName
      ]),
    [disputes, disputesSearchQuery]
  );

  const filteredStaff = useMemo(() => {
    let rows = staffList || [];
    if (staffStatusFilter === 'active') rows = rows.filter((s) => s.isActive);
    else if (staffStatusFilter === 'inactive') rows = rows.filter((s) => !s.isActive);
    return filterBySearch(rows, staffSearchQuery, (s) => [
      s.name,
      s.email,
      ...(s.permissions || [])
    ]);
  }, [staffList, staffSearchQuery, staffStatusFilter]);

  const usersStartIndex = (usersPage - 1) * itemsPerPage;
  const usersEndIndex = usersStartIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(usersStartIndex, usersEndIndex);
  const totalUsersPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));

  const walletsStartIndex = (walletsPage - 1) * itemsPerPage;
  const walletsEndIndex = walletsStartIndex + itemsPerPage;
  const paginatedWallets = filteredWallets.slice(walletsStartIndex, walletsEndIndex);
  const totalWalletsPages = Math.max(1, Math.ceil(filteredWallets.length / itemsPerPage));

  const reportsStartIndex = (reportsPage - 1) * itemsPerPage;
  const reportsEndIndex = reportsStartIndex + itemsPerPage;
  const paginatedReports = filteredReports.slice(reportsStartIndex, reportsEndIndex);
  const totalReportsPages = Math.max(1, Math.ceil(filteredReports.length / itemsPerPage));

  const getPageTitle = () => {
    switch (activeTab) {
      case 'users': return 'Users';
      case 'wallets': return 'Wallets';
      case 'reports': return 'Reports';
      case 'subscription-plans': return 'Subscription Plans';
      case 'bills': return 'Bills';
      case 'staff': return 'Staff';
      case 'analytics': return 'Analytics';
      case 'logs': return 'Audit logs';
      case 'disputes': return 'Disputes';
      case 'demographics-options': return 'Demographics options';
      case 'settings': return 'Account';
      default: return 'Overview';
    }
  };

  return (
    <div>
      {activeTab !== 'overview' && (
        <h1 style={{ 
          fontSize: '1.5rem', 
          fontWeight: '600', 
          color: 'var(--text-primary)', 
          margin: '0 0 8px 0',
          paddingTop: '0'
        }}>
          {getPageTitle()}
        </h1>
      )}
      {loading && activeTab === 'overview' && <DashboardSectionLoader />}
      {activeTab === 'overview' && stats && !loading && (
        <>
        <div className="quick-actions">
          <Link to="/dashboard?tab=users" className="quick-action-btn">
            <span className="quick-action-icon"><PeopleIcon sx={{ fontSize: 28 }} /></span>
            <span>Users</span>
          </Link>
          <Link to="/dashboard?tab=wallets" className="quick-action-btn">
            <span className="quick-action-icon"><AccountBalanceWalletIcon sx={{ fontSize: 28 }} /></span>
            <span>Wallets</span>
          </Link>
          <Link to="/dashboard?tab=reports" className="quick-action-btn">
            <span className="quick-action-icon"><AssessmentIcon sx={{ fontSize: 28 }} /></span>
            <span>Reports</span>
          </Link>
          <Link to="/dashboard?tab=disputes" className="quick-action-btn">
            <span className="quick-action-icon"><GavelIcon sx={{ fontSize: 28 }} /></span>
            <span>Disputes</span>
          </Link>
          <Link to="/dashboard?tab=subscription-plans" className="quick-action-btn">
            <span className="quick-action-icon"><CreditCardIcon sx={{ fontSize: 28 }} /></span>
            <span>Plans</span>
          </Link>
          <Link to="/dashboard?tab=analytics" className="quick-action-btn">
            <span className="quick-action-icon"><BarChartIcon sx={{ fontSize: 28 }} /></span>
            <span>Analytics</span>
          </Link>
        </div>
        <div className="grid grid-3">
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Total Users</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>{stats.totalUsers}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Active accounts</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>Labs</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>{stats.totalLabs}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Registered labs</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Clinics</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>{stats.totalClinics}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Partner clinics</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(181, 255, 51, 0.1), rgba(181, 255, 51, 0.05))',
            border: '1px solid rgba(181, 255, 51, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-lime-green)' }}>Phlebotomists</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-lime-green)', margin: '16px 0 0 0' }}>{stats.totalPhlebotomists}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Active phlebotomists</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))',
            border: '1px solid rgba(51, 255, 217, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Suppliers</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>{stats.totalSuppliers}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Registered suppliers</p>
          </div>
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 193, 7, 0.05))',
            border: '1px solid rgba(255, 193, 7, 0.2)'
          }}>
            <h3 className="card-title" style={{ color: '#FFC107' }}>Pending Reports</h3>
            <p style={{ fontSize: '3rem', fontWeight: '700', color: '#FFC107', margin: '16px 0 0 0' }}>{stats.pendingReports}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Awaiting processing</p>
          </div>
        </div>
        </>
      )}

      {activeTab === 'users' && (
        <div>
          <Modal
            isOpen={showUserDetails && !!selectedUser}
            onClose={() => { setShowUserDetails(false); setSelectedUser(null); }}
            title="User details"
            wide
          >
            {selectedUser && (
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <UserAvatar photoUrl={selectedUser.profilePhotoUrl} label={userDisplayLabel(selectedUser)} size={64} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Name</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedUser.name}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Email</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedUser.email}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Role</p>
                    <span className="badge badge-info">{selectedUser.role}</span>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Status</p>
                    <span className={`badge ${selectedUser.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {selectedUser.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </Modal>
          <div className="card">
            <h2 className="card-title" style={{ margin: '0 0 4px 0' }}>Users</h2>
            <TableToolbar
              search={userSearchQuery}
              onSearchChange={setUserSearchQuery}
              placeholder="Search name, email, lab/clinic/company…"
              resultCount={filteredUsers.length}
              totalCount={roleAndStatusUsers.length}
            >
              <select
                className="form-select"
                value={userFilterRole}
                onChange={(e) => { setUserFilterRole(e.target.value); setUsersPage(1); }}
                aria-label="Filter by role"
              >
                <option value="">All roles</option>
                <option value="admin">Admin</option>
                <option value="lab">Lab</option>
                <option value="clinic">Clinic</option>
                <option value="phlebotomist">Phlebotomist</option>
                <option value="supplier">Supplier</option>
                <option value="lab_employee">Lab employee</option>
              </select>
              <select
                className="form-select"
                value={userFilterStatus}
                onChange={(e) => { setUserFilterStatus(e.target.value); setUsersPage(1); }}
                aria-label="Filter by status"
              >
                <option value="">All status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </TableToolbar>
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '52px' }} aria-label="Photo" />
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <UserAvatar photoUrl={user.profilePhotoUrl} label={userDisplayLabel(user)} size={36} />
                      </td>
                      <td style={{ fontWeight: '500' }}>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className="badge badge-info">{user.role}</span>
                      </td>
                      <td>
                        <span className={`badge ${user.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-outline"
                            onClick={() => handleViewUserDetails(user)}
                            style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-outline"
                            style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                            onClick={() => handleUserStatusToggle(user._id, user.isActive)}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-mobile-card">
              {paginatedUsers.map((user) => (
                <div key={user._id} className="table-mobile-card-item">
                <div className="table-mobile-card-row" style={{ alignItems: 'center', gap: '12px' }}>
                  <UserAvatar photoUrl={user.profilePhotoUrl} label={userDisplayLabel(user)} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span className="table-mobile-card-label">Name</span>
                    <span className="table-mobile-card-value" style={{ fontWeight: '500', display: 'block' }}>{user.name}</span>
                  </div>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Email</span>
                  <span className="table-mobile-card-value">{user.email}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Role</span>
                  <span className="table-mobile-card-value">
                    <span className="badge badge-info">{user.role}</span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Status</span>
                  <span className="table-mobile-card-value">
                    <span className={`badge ${user.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Actions</span>
                  <span className="table-mobile-card-value">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                      <button
                        className="btn btn-outline"
                        onClick={() => handleViewUserDetails(user)}
                        style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                        onClick={() => handleUserStatusToggle(user._id, user.isActive)}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </span>
                </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={usersPage}
              totalPages={totalUsersPages}
              onPageChange={setUsersPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredUsers.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '8px' }}>Audit logs</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Sign ups, logins, and admin changes (users, plans, wallets, reports, and more). Use filters and search; results are paginated on the server.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '12px',
              marginBottom: '16px',
              alignItems: 'end'
            }}
          >
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Action</label>
              <select
                className="form-select"
                value={logActionFilter}
                onChange={(e) => setLogActionFilter(e.target.value)}
              >
                {AUDIT_ACTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Resource type</label>
              <select
                className="form-select"
                value={logResourceFilter}
                onChange={(e) => setLogResourceFilter(e.target.value)}
              >
                <option value="all">All types</option>
                {auditResourceTypes.map((rt) => (
                  <option key={rt} value={rt}>{rt}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">From date</label>
              <input
                type="date"
                className="form-input"
                value={logDateFrom}
                onChange={(e) => setLogDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">To date</label>
              <input
                type="date"
                className="form-input"
                value={logDateTo}
                onChange={(e) => setLogDateTo(e.target.value)}
              />
            </div>
          </div>
          <TableToolbar
            search={logsSearchQuery}
            onSearchChange={setLogsSearchQuery}
            placeholder="Search summary, resource, actor email/name…"
            resultCount={crudLogs.length}
            totalCount={logsTotal}
          />
          {logsLoading ? (
            <DashboardSectionLoader minHeight={220} />
          ) : (
            <>
              <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Action</th>
                      <th>Resource</th>
                      <th>Resource ID</th>
                      <th>Actor</th>
                      <th>Role</th>
                      <th>IP</th>
                      <th>Summary / details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crudLogs.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>
                          No log entries match your filters.
                        </td>
                      </tr>
                    ) : (
                      crudLogs.map((log) => (
                        <tr key={log._id}>
                          <td style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                            {formatLocalDateTime(log.timestamp)}
                          </td>
                          <td>
                            <span className={`badge ${auditBadgeClass(log.actionKey)}`}>{log.action}</span>
                          </td>
                          <td style={{ fontSize: '0.875rem' }}>{log.entity}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', maxWidth: '120px' }}>
                            {log.entityId || '—'}
                          </td>
                          <td style={{ fontSize: '0.875rem' }}>
                            <div style={{ fontWeight: 500 }}>{log.userName}</div>
                            {log.actorEmail && log.actorEmail !== log.userName && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{log.actorEmail}</div>
                            )}
                          </td>
                          <td><span className="badge badge-info">{log.actorRole || '—'}</span></td>
                          <td style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{log.ip || '—'}</td>
                          <td style={{ fontSize: '0.8125rem', maxWidth: '320px', wordBreak: 'break-word' }}>{log.details || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {logsTotal > 0 && (
                <Pagination
                  currentPage={logsPage}
                  totalPages={logsTotalPages}
                  onPageChange={setLogsPage}
                  itemsPerPage={LOGS_PAGE_SIZE}
                  totalItems={logsTotal}
                />
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'wallets' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>Wallets</h2>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {wallets.length}
            </div>
          </div>
          <TableToolbar
            search={walletSearchQuery}
            onSearchChange={setWalletSearchQuery}
            placeholder="Search phlebotomist, balance…"
            resultCount={filteredWallets.length}
            totalCount={wallets.length}
          />
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Phlebotomist</th>
                  <th>Balance</th>
                  <th>Total Earnings</th>
                  <th>Total Paid</th>
                  <th>Pending Transactions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedWallets.map((wallet) => {
                  const pendingCount = (wallet.transactions || []).filter(t => t.status === 'pending').length;
                  return (
                    <tr key={wallet._id}>
                      <td style={{ fontWeight: '500' }}>{wallet.phlebotomist?.name || 'N/A'}</td>
                      <td style={{ color: 'var(--logo-cyan-teal)', fontWeight: '700', fontSize: '1.1rem' }}>
                        ${wallet.balance.toFixed(2)}
                      </td>
                      <td>${wallet.totalEarnings.toFixed(2)}</td>
                      <td>${wallet.totalPaid.toFixed(2)}</td>
                      <td>
                        <span className="badge badge-warning">{pendingCount}</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                          onClick={() =>
                            setPayoutModal({
                              phlebotomistId: wallet.phlebotomist._id,
                              name: wallet.phlebotomist?.name || 'Phlebotomist'
                            })}
                        >
                          Payout
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="table-mobile-card">
            {paginatedWallets.map((wallet) => {
              const pendingCount = (wallet.transactions || []).filter(t => t.status === 'pending').length;
              return (
                <div key={wallet._id} className="table-mobile-card-item">
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Phlebotomist</span>
                    <span className="table-mobile-card-value" style={{ fontWeight: '500' }}>{wallet.phlebotomist?.name || 'N/A'}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Balance</span>
                    <span className="table-mobile-card-value" style={{ color: 'var(--logo-cyan-teal)', fontWeight: '700', fontSize: '1.1rem' }}>
                      ${wallet.balance.toFixed(2)}
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Total Earnings</span>
                    <span className="table-mobile-card-value">${wallet.totalEarnings.toFixed(2)}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Total Paid</span>
                    <span className="table-mobile-card-value">${wallet.totalPaid.toFixed(2)}</span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Pending</span>
                    <span className="table-mobile-card-value">
                      <span className="badge badge-warning">{pendingCount}</span>
                    </span>
                  </div>
                  <div className="table-mobile-card-row">
                    <span className="table-mobile-card-label">Actions</span>
                    <span className="table-mobile-card-value">
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                        onClick={() =>
                          setPayoutModal({
                            phlebotomistId: wallet.phlebotomist._id,
                            name: wallet.phlebotomist?.name || 'Phlebotomist'
                          })}
                      >
                        Payout
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination
            currentPage={walletsPage}
            totalPages={totalWalletsPages}
            onPageChange={setWalletsPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredWallets.length}
          />
        </div>
      )}

      {activeTab === 'reports' && (
        <div>
          <Modal
            isOpen={showReportDetails && !!selectedReport}
            onClose={() => { setShowReportDetails(false); setSelectedReport(null); }}
            title="Report details"
            wide
          >
            {selectedReport && (
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Report Number</p>
                    <p style={{ margin: 0, fontWeight: '500', fontFamily: 'monospace' }}>{selectedReport.reportNumber}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Patient</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedReport.patientName}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Lab</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedReport.lab?.labName || selectedReport.lab?.name}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Clinic</p>
                    <p style={{ margin: 0, fontWeight: '500' }}>{selectedReport.clinic?.clinicName || selectedReport.clinic?.name}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Payment status</p>
                    <span className={`badge ${
                      selectedReport.billingStatus === 'paid' ? 'badge-success' :
                      selectedReport.billingStatus === 'billed' ? 'badge-info' : 'badge-warning'
                    }`}>
                      {selectedReport.billingStatus}
                    </span>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>Amount</p>
                    <p style={{ margin: 0, fontWeight: '600' }}>${selectedReport.billingAmount || 0}</p>
                  </div>
                </div>
                {selectedReport.billingStatus !== 'billed' && selectedReport.billingStatus !== 'paid' && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        handleMarkReportBilled(selectedReport._id);
                        setShowReportDetails(false);
                        setSelectedReport(null);
                      }}
                    >
                      Mark billed
                    </button>
                  </div>
                )}
              </div>
            )}
          </Modal>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Reports</h2>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                {reports.length}
              </div>
            </div>
            <TableToolbar
              search={reportSearchQuery}
              onSearchChange={setReportSearchQuery}
              placeholder="Search reports, patient, lab…"
              resultCount={filteredReports.length}
              totalCount={reports.length}
            />
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Report Number</th>
                    <th>Patient</th>
                    <th>Lab</th>
                    <th>Clinic</th>
                    <th>Payment status</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReports.map((report) => (
                    <tr key={report._id}>
                      <td style={{ fontWeight: '500', fontFamily: 'monospace' }}>{report.reportNumber}</td>
                      <td>{report.patientName}</td>
                      <td>{report.lab?.labName || report.lab?.name}</td>
                      <td>{report.clinic?.clinicName || report.clinic?.name}</td>
                      <td>
                        <span className={`badge ${
                          report.billingStatus === 'paid' ? 'badge-success' :
                          report.billingStatus === 'billed' ? 'badge-info' : 'badge-warning'
                        }`}>
                          {report.billingStatus}
                        </span>
                      </td>
                      <td style={{ fontWeight: '600' }}>${report.billingAmount || 0}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-outline"
                            onClick={() => handleViewReportDetails(report)}
                            style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                          >
                            View
                          </button>
                          {report.billingStatus !== 'billed' && report.billingStatus !== 'paid' && (
                            <button
                              className="btn btn-outline"
                              style={{ padding: '6px 16px', fontSize: '0.875rem', minHeight: '44px' }}
                              onClick={() => handleMarkReportBilled(report._id)}
                            >
                              Mark Billed
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-mobile-card">
              {paginatedReports.map((report) => (
                <div key={report._id} className="table-mobile-card-item">
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Report #</span>
                  <span className="table-mobile-card-value" style={{ fontWeight: '500', fontFamily: 'monospace' }}>{report.reportNumber}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Patient</span>
                  <span className="table-mobile-card-value">{report.patientName}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Lab</span>
                  <span className="table-mobile-card-value">{report.lab?.labName || report.lab?.name}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Clinic</span>
                  <span className="table-mobile-card-value">{report.clinic?.clinicName || report.clinic?.name}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Status</span>
                  <span className="table-mobile-card-value">
                    <span className={`badge ${
                      report.billingStatus === 'paid' ? 'badge-success' :
                      report.billingStatus === 'billed' ? 'badge-info' : 'badge-warning'
                    }`}>
                      {report.billingStatus}
                    </span>
                  </span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Amount</span>
                  <span className="table-mobile-card-value" style={{ fontWeight: '600' }}>${report.billingAmount || 0}</span>
                </div>
                <div className="table-mobile-card-row">
                  <span className="table-mobile-card-label">Actions</span>
                  <span className="table-mobile-card-value">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                      <button
                        className="btn btn-outline"
                        onClick={() => handleViewReportDetails(report)}
                        style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                      >
                        View
                      </button>
                      {report.billingStatus !== 'billed' && report.billingStatus !== 'paid' && (
                        <button
                          className="btn btn-outline"
                          style={{ padding: '8px 16px', fontSize: '0.875rem', minHeight: '44px', width: '100%' }}
                          onClick={() => handleMarkReportBilled(report._id)}
                        >
                          Mark Billed
                        </button>
                      )}
                    </div>
                  </span>
                </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={reportsPage}
              totalPages={totalReportsPages}
              onPageChange={setReportsPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredReports.length}
            />
          </div>
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>All Bills</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Bills sent by Admin, Phlebotomists, and Suppliers to Labs.</p>
          <TableToolbar
            search={billsSearchQuery}
            onSearchChange={setBillsSearchQuery}
            placeholder="Search invoice, biller, lab, status…"
            resultCount={filteredBills.length}
            totalCount={bills?.length || 0}
          />
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr><th>Invoice</th><th>From</th><th>To (Lab)</th><th>Amount</th><th>Type</th><th>Status</th><th>Date</th></tr>
              </thead>
              <tbody>
                {filteredBills.map((b) => (
                  <tr key={b._id}>
                    <td style={{ fontWeight: '500' }}>{b.invoiceNumber}</td>
                    <td>{b.biller?.name || b.biller?.companyName}</td>
                    <td>{b.lab?.labName || b.lab?.name}</td>
                    <td style={{ fontWeight: '600' }}>${b.amount?.toLocaleString()}</td>
                    <td><span className="badge badge-info">{b.type?.replace('_', ' ') || 'custom'}</span></td>
                    <td><span className={`badge ${b.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span></td>
                    <td style={{ fontSize: '0.875rem' }}>{b.createdAt ? formatLocalDate(b.createdAt) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!filteredBills || filteredBills.length === 0) && <p style={{ color: 'var(--text-secondary)', padding: '24px', textAlign: 'center' }}>No bills match</p>}
        </div>
      )}

      {activeTab === 'subscription-plans' && subscriptionPlans && (
        <>
          <Modal
            isOpen={editPlanOpen && !!editPlan}
            onClose={() => { setEditPlanOpen(false); setEditPlan(null); setEditPlanMessage(''); }}
            title="Edit subscription plan"
            wide
          >
            {editPlan && (
              <form onSubmit={handleSaveEditPlan} style={{ display: 'grid', gap: '12px', maxWidth: '720px' }}>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0 }}>
                  Role: <strong>{editPlan.role}</strong> (cannot be changed).
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Name</label>
                    <input className="form-input" value={editPlan.name} onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Tier type</label>
                    <select className="form-select" value={editPlan.type} onChange={(e) => setEditPlan({ ...editPlan, type: e.target.value })}>
                      <option value="basic">basic</option>
                      <option value="pro">pro</option>
                      <option value="custom">custom</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0, alignSelf: 'end' }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editPlan.isActive} onChange={(e) => setEditPlan({ ...editPlan, isActive: e.target.checked })} />
                      Active
                    </label>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">$/mo</label>
                    <input type="number" min="0" className="form-input" value={editPlan.priceMonthly} onChange={(e) => setEditPlan({ ...editPlan, priceMonthly: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">$/yr</label>
                    <input type="number" min="0" className="form-input" value={editPlan.priceYearly} onChange={(e) => setEditPlan({ ...editPlan, priceYearly: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Yearly % off</label>
                    <input type="number" min="0" className="form-input" value={editPlan.yearlyDiscountPercent} onChange={(e) => setEditPlan({ ...editPlan, yearlyDiscountPercent: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Trial days</label>
                    <input type="number" min="0" className="form-input" value={editPlan.trialDays} onChange={(e) => setEditPlan({ ...editPlan, trialDays: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Clinics limit</label>
                    <input type="number" min="0" className="form-input" value={editPlan.clinicsLimit} onChange={(e) => setEditPlan({ ...editPlan, clinicsLimit: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Jobs/mo</label>
                    <input type="number" min="0" className="form-input" value={editPlan.jobPostingLimit} onChange={(e) => setEditPlan({ ...editPlan, jobPostingLimit: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Offers/mo</label>
                    <input type="number" min="0" className="form-input" value={editPlan.offerLimit} onChange={(e) => setEditPlan({ ...editPlan, offerLimit: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Bid limit</label>
                    <input type="number" min="0" className="form-input" value={editPlan.biddingLimit} onChange={(e) => setEditPlan({ ...editPlan, biddingLimit: e.target.value })} />
                  </div>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Features (one per line)</label>
                  <textarea className="form-textarea" rows={4} value={editPlan.features} onChange={(e) => setEditPlan({ ...editPlan, features: e.target.value })} />
                </div>
                {editPlanMessage && <p style={{ fontSize: '0.875rem', margin: 0 }}>{editPlanMessage}</p>}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button type="submit" className="btn btn-primary">Save changes</button>
                  <button type="button" className="btn btn-outline" onClick={() => { setEditPlanOpen(false); setEditPlan(null); }}>Cancel</button>
                </div>
              </form>
            )}
          </Modal>
        <div style={{ display: 'grid', gap: '24px' }}>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '8px' }}>Add subscription plan</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Add plans and prices for each audience. Only active plans are shown on the public pricing page. Enter $0 for both monthly and yearly if a tier should be free.
            </p>
            <form onSubmit={handleCreateSubscriptionPlan} style={{ display: 'grid', gap: '12px', maxWidth: '640px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Name</label>
                  <input className="form-input" value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} required />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Role</label>
                  <select className="form-select" value={newPlan.role} onChange={(e) => setNewPlan({ ...newPlan, role: e.target.value })}>
                    <option value="lab">Lab</option>
                    <option value="supplier">Supplier</option>
                    <option value="phlebotomist">Phlebotomist</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Tier type</label>
                  <select className="form-select" value={newPlan.type} onChange={(e) => setNewPlan({ ...newPlan, type: e.target.value })}>
                    <option value="basic">basic</option>
                    <option value="pro">pro</option>
                    <option value="custom">custom</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">$/mo</label>
                  <input type="number" min="0" className="form-input" value={newPlan.priceMonthly} onChange={(e) => setNewPlan({ ...newPlan, priceMonthly: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">$/yr</label>
                  <input type="number" min="0" className="form-input" value={newPlan.priceYearly} onChange={(e) => setNewPlan({ ...newPlan, priceYearly: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Yearly % off</label>
                  <input type="number" min="0" className="form-input" value={newPlan.yearlyDiscountPercent} onChange={(e) => setNewPlan({ ...newPlan, yearlyDiscountPercent: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Trial days</label>
                  <input type="number" min="0" className="form-input" value={newPlan.trialDays} onChange={(e) => setNewPlan({ ...newPlan, trialDays: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Clinics limit</label>
                  <input type="number" min="0" className="form-input" value={newPlan.clinicsLimit} onChange={(e) => setNewPlan({ ...newPlan, clinicsLimit: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Jobs/mo</label>
                  <input type="number" min="0" className="form-input" value={newPlan.jobPostingLimit} onChange={(e) => setNewPlan({ ...newPlan, jobPostingLimit: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Offers/mo</label>
                  <input type="number" min="0" className="form-input" value={newPlan.offerLimit} onChange={(e) => setNewPlan({ ...newPlan, offerLimit: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Bid limit</label>
                  <input type="number" min="0" className="form-input" value={newPlan.biddingLimit} onChange={(e) => setNewPlan({ ...newPlan, biddingLimit: e.target.value })} />
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Features (one per line)</label>
                <textarea className="form-textarea" rows={4} value={newPlan.features} onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })} />
              </div>
              {planFormMessage && <p style={{ fontSize: '0.875rem', margin: 0 }}>{planFormMessage}</p>}
              <button type="submit" className="btn btn-primary" style={{ justifySelf: 'start' }}>Create plan</button>
            </form>
          </div>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '16px' }}>Lab Plans</h2>
            {(!(subscriptionPlans.lab || []).length) && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>No lab plans yet. Create one above.</p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {(subscriptionPlans.lab || []).map((plan) => (
                <div key={plan._id} style={{ padding: '20px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)' }}>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0' }}>{plan.name}</h3>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 12px 0' }}>${plan.priceMonthly ?? plan.price ?? 0}/mo</p>
                  {plan.priceYearly > 0 && <p style={{ fontSize: '0.875rem', color: 'var(--logo-lime-green)', margin: '0 0 8px 0' }}>${plan.priceYearly}/yr {plan.yearlyDiscountPercent ? `(${plan.yearlyDiscountPercent}% off)` : ''}</p>}
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Clinics: {plan.clinicsLimit} · Jobs/mo: {plan.jobPostingLimit}</p>
                  {plan.trialDays > 0 && <p style={{ fontSize: '0.8125rem', color: 'var(--logo-lime-green)', marginBottom: '8px' }}>{plan.trialDays}-day free trial</p>}
                  <SubscriptionPlanStripePanel plan={plan} />
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem' }}>
                    {(plan.features || []).map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <button type="button" className="btn btn-outline" style={{ marginTop: '12px', width: '100%' }} onClick={() => openEditPlan(plan)}>Edit plan</button>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '16px' }}>Supplier Plans</h2>
            {(!(subscriptionPlans.supplier || []).length) && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>No supplier plans yet.</p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {(subscriptionPlans.supplier || []).map((plan) => (
                <div key={plan._id} style={{ padding: '20px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)' }}>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0' }}>{plan.name}</h3>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 12px 0' }}>${plan.priceMonthly ?? plan.price ?? 0}/mo</p>
                  {plan.priceYearly > 0 && <p style={{ fontSize: '0.875rem', color: 'var(--logo-lime-green)', margin: '0 0 8px 0' }}>${plan.priceYearly}/yr</p>}
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Offers/mo: {plan.offerLimit}</p>
                  {plan.trialDays > 0 && <p style={{ fontSize: '0.8125rem', color: 'var(--logo-lime-green)', marginBottom: '8px' }}>{plan.trialDays}-day free trial</p>}
                  <SubscriptionPlanStripePanel plan={plan} />
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem' }}>{(plan.features || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
                  <button type="button" className="btn btn-outline" style={{ marginTop: '12px', width: '100%' }} onClick={() => openEditPlan(plan)}>Edit plan</button>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '16px' }}>Phlebotomist Plans</h2>
            {(!(subscriptionPlans.phlebotomist || []).length) && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>No phlebotomist plans yet.</p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {(subscriptionPlans.phlebotomist || []).map((plan) => (
                <div key={plan._id} style={{ padding: '20px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)' }}>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: '0 0 8px 0' }}>{plan.name}</h3>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 12px 0' }}>${plan.priceMonthly ?? plan.price ?? 0}/mo</p>
                  {plan.priceYearly > 0 && <p style={{ fontSize: '0.875rem', color: 'var(--logo-lime-green)', margin: '0 0 8px 0' }}>${plan.priceYearly}/yr</p>}
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Bidding limit: {plan.biddingLimit}</p>
                  {plan.trialDays > 0 && <p style={{ fontSize: '0.8125rem', color: 'var(--logo-lime-green)', marginBottom: '8px' }}>{plan.trialDays}-day free trial</p>}
                  <SubscriptionPlanStripePanel plan={plan} />
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem' }}>{(plan.features || []).map((f, i) => <li key={i}>{f}</li>)}</ul>
                  <button type="button" className="btn btn-outline" style={{ marginTop: '12px', width: '100%' }} onClick={() => openEditPlan(plan)}>Edit plan</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        </>
      )}

      {activeTab === 'staff' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Internal Staff</h2>
          <TableToolbar
            search={staffSearchQuery}
            onSearchChange={setStaffSearchQuery}
            placeholder="Search name, email, permissions…"
            resultCount={filteredStaff.length}
            totalCount={(staffList || []).length}
          >
            <select
              className="form-select"
              value={staffStatusFilter}
              onChange={(e) => setStaffStatusFilter(e.target.value)}
              aria-label="Filter staff by status"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </TableToolbar>
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Permissions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((staff) => (
                  <tr key={staff._id}>
                    <td style={{ fontWeight: '500' }}>{staff.name}</td>
                    <td>{staff.email}</td>
                    <td><span style={{ fontSize: '0.875rem' }}>{staff.permissions?.join(', ')}</span></td>
                    <td><span className={`badge ${staff.isActive ? 'badge-success' : 'badge-danger'}`}>{staff.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <button className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem' }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '16px' }}>Staff can: manual payment recording, settlement updates, user monitoring.</p>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="grid grid-3" style={{ gap: '24px' }}>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Users Over Time</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.usersOverTime || []).map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Revenue by Month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.revenueByMonth || []).map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-lime-green)' }}>${d.amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Jobs by Status</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.jobsByStatus || []).map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.status}</span>
                  <span className="badge badge-info">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Top Labs (by job count)</h3>
            <div className="table-wrapper">
              <table className="table">
                <thead><tr><th>Lab</th><th>Jobs</th></tr></thead>
                <tbody>
                  {(analytics?.topLabs || []).map((lab, i) => (
                    <tr key={i}><td style={{ fontWeight: '500' }}>{lab.name}</td><td>{lab.jobs}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'disputes' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Disputes</h2>
          <TableToolbar
            search={disputesSearchQuery}
            onSearchChange={setDisputesSearchQuery}
            placeholder="Search disputes…"
            resultCount={filteredDisputes.length}
            totalCount={disputes?.length || 0}
          />
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredDisputes.map((d) => (
              <div key={d._id} style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', color: 'var(--text-primary)' }}>{d.title}</h3>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{d.description}</p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Raised by: {d.raisedBy?.name || d.raisedBy?.labName} · Against: {d.against?.name || d.against?.companyName || 'N/A'} · {formatLocalDate(d.createdAt)}
                    </p>
                  </div>
                  <span className={`badge ${
                    d.status === 'resolved' ? 'badge-success' :
                    d.status === 'under_review' ? 'badge-info' : 'badge-warning'
                  }`}>{d.status}</span>
                </div>
                {(d.status === 'open' || d.status === 'under_review') && (
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: '0.875rem' }}>Resolve</button>
                    <button className="btn btn-outline" style={{ padding: '6px 16px', fontSize: '0.875rem' }}>Escalate</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'demographics-options' && <AdminDemographicsOptionsPanel />}

      {activeTab === 'settings' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          <AccountSettingsPanel
            user={authUser}
            refreshUser={refreshUser}
            onMessage={(m) => {
              if (!m) return;
              const s = String(m).trim();
              if (/^error\s*:/i.test(s)) showError(s.replace(/^error\s*:\s*/i, ''));
              else showSuccess(s);
            }}
          />
        </div>
      )}

      <PromptModal
        isOpen={!!payoutModal}
        onClose={() => setPayoutModal(null)}
        title="Process payout"
        label={payoutModal ? `Amount (USD) — ${payoutModal.name}` : 'Amount (USD)'}
        placeholder="0.00"
        inputType="number"
        submitLabel="Process payout"
        onSubmit={(raw) =>
          payoutModal ? handleProcessPayout(payoutModal.phlebotomistId, raw) : Promise.resolve(false)}
      />
    </div>
  );
};

export default AdminDashboard;
